import { Component, Input, ViewChild, ElementRef, Renderer2, OnInit, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { ActivatedRoute, RouterModule } from "@angular/router";
import { ItemConfigBase, IItemDetail, ItemConfigTypes, DropdownItemConfig } from "../../../interfaces/iitemconfig";
import { DropDownItem } from "../../../models/dropdown";
import { IDropDownView, IDropDownItemView } from "../../../interfaces/idropdowns";
import { CountryDataStatus } from "../../../enums/countrydatastatus";
import { SourceMetadata } from "../../../models/metadata";
import { AppSettings } from "../../../appsettings";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditorModule } from "@ckeditor/ckeditor5-angular";
import { CkEditorEnhancedDirective } from "../../../directives/ck-editor-enhanced.directive";
import { HtmlContentModalComponent } from "../../htmlcontentmodal/htmlcontentmodal.component";
import { MetaDataItemSelector } from "../../metadata/metadataitemselector.component";
import { NgSelectComponent } from "../../shared/ng-select/ng-select.component";

@Component({
    selector: "itemconfigmanagementitem",
    templateUrl: "./itemconfigmanagementitem.component.html",
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, CKEditorModule, CkEditorEnhancedDirective, HtmlContentModalComponent, MetaDataItemSelector, NgSelectComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class ItemConfigManagementItemComponent implements OnInit {
    public Editor = ClassicEditor;
    @Input() item!: ItemConfigBase;
    @Input() items!: ItemConfigBase[];
    @Input() liveItems!: IItemDetail[];
    @Input() depth!: number;
    @Input() statusId!: number;
    @Input() canEdit!: boolean;
    @Input() dropdowns!: IDropDownView[];
    itemMetadata!: SourceMetadata[];
    countryDataStatus: any = CountryDataStatus;
    @ViewChild("ckeditor") ckeditor!: ElementRef;
    documentClickListener!: Function;
    editMode: boolean = false;
    selectedItemDetailLiveItem!: IItemDetail | null;
    selectedItemDetailCurrentItem!: IItemDetail | null;
    @ViewChild("htmlcontentmodal") htmlContentModal: any;
    originalValue!: string;
    config: any = {};
    dropdownItems: DropDownItem[] = [];
    selectedDropdownItem!: DropDownItem;
    multipleSelectedDropdownItems: DropDownItem[] = [];
    multipleSelectionString: string = "";
    public itemConfigType = ItemConfigTypes;


    constructor(
        private renderer: Renderer2,
        private activatedRoute: ActivatedRoute,
        private cdRef: ChangeDetectorRef
    ) {}

    ngOnInit(): void {

        this.itemMetadata = this.activatedRoute.snapshot.data["itemmetadata"];

        this.config = AppSettings.ckeditorConfig;
        this.originalValue = this.item.itemDetail.value;
        if (this.item.itemConfigType === ItemConfigTypes.DropdownItemConfig) {
            // get dropdown based on item config
            let dropDownItem: DropdownItemConfig = (this.item as DropdownItemConfig);
            let dropdownId: number = dropDownItem.dropdownId;
            let dropdown = this.dropdowns.find((i: IDropDownView) => i.id === dropdownId);
            if (dropdown) {
                // now we have the corresponding dropdown for this item config, populate the list of items to render on screen
                dropdown.items.forEach((i: IDropDownItemView) => {
                    if (!i.disabled && i.id !== undefined && i.text !== undefined) {
                        this.dropdownItems.push(new DropDownItem(i.id as number, i.text, i.code));
                    }
                });
            }
            if (dropDownItem.allowMultiple) {
                if (this.item.itemDetail.value) {
                    // set selected value
                    const idArr: string[] = this.item.itemDetail.value.split(",");
                    idArr.forEach(id => {
                        const ddi = this.dropdownItems.find((i: DropDownItem) => i.id === Number(id));
                        if (ddi) {
                            this.multipleSelectedDropdownItems.push(ddi);
                        } else {
                            this.item.itemDetail.modified = true;
                        }
                    });
                    this.resyncMultipleSelectionsWithItemDetail();
                }
                // Don't add "No item selected" placeholder to the active items array
                // The ng-select component will handle the placeholder display
                this.refreshMultipleSelectionString();
            } else {
                if (this.item.itemDetail.value) {
                    // set selected value
                    const found = this.dropdownItems.find((i: DropDownItem) => i.id === Number(this.item.itemDetail.value));
                    if (found) {
                        this.selectedDropdownItem = found;
                    }
                }
                if (!this.selectedDropdownItem) {
                    this.selectedDropdownItem = new DropDownItem(-1, "No item selected", "");
                }
            }
        }
    }

    onDropDownItemSelected(dropdownItem: DropDownItem): void {
        this.selectedDropdownItem = dropdownItem;
        this.item.itemDetail.value = this.selectedDropdownItem.id.toString();
        const found = this.dropdownItems.find(i => i.id === dropdownItem.id);
        this.item.itemDetail.codes = found ? found.code : "";
        this.itemValueChanged(null);
    }

    onDropDownMultipleItemSelected(dropdownItem: DropDownItem): void {
        // Check if item is already selected - toggle behavior
        const alreadySelected = this.multipleSelectedDropdownItems.find(i => i.id === dropdownItem.id);
        if (alreadySelected) {
            // Item already selected, remove it (toggle off)
            this.removeItem(dropdownItem);
            return;
        }
        
        // Item not selected, add it
        const found = this.dropdownItems.find(i => i.id === dropdownItem.id);
        if (found) {
            this.multipleSelectedDropdownItems.push(found);
        }
        this.refreshMultipleSelectionString();
        this.resyncMultipleSelectionsWithItemDetail();
        this.itemValueChanged(null);
    }

    resetDropDown(): void {
        this.selectedDropdownItem = new DropDownItem(-1, "No item selected", "");
        this.item.itemDetail.value = "";
        this.itemValueChanged(null);
    }

    removeItem(dropdownItem: DropDownItem): void {
        const target = this.multipleSelectedDropdownItems.find(item => item.id === dropdownItem.id);
        const index: number = this.multipleSelectedDropdownItems.indexOf(target!);
        if (index > -1) {
            this.multipleSelectedDropdownItems.splice(index, 1);
        }
        this.refreshMultipleSelectionString();
        this.resyncMultipleSelectionsWithItemDetail();
        this.itemValueChanged(null);
    }

    clearMultipleSelection(): void {
        // Clear all selected items
        this.multipleSelectedDropdownItems = [];
        this.item.itemDetail.value = "";
        this.item.itemDetail.codes = "";
        this.multipleSelectionString = "";
        this.itemValueChanged(null);
    }

    /**
     *    Enables editing of item
     */
    onInitiateChanges(): void {
        this.editMode = this.statusId === CountryDataStatus.Draft && this.canEdit;
        this.cdRef.detectChanges();

        // Fix CKEditor link dialog when entering edit mode
        if (this.editMode && (this.item.itemConfigType === this.itemConfigType.CategoryItemConfig || this.item.itemConfigType === this.itemConfigType.HtmlTextItemConfig)) {
            this.fixCKEditorLinkDialog();
        }

        // use mousedown as click wont work with the drag
        this.documentClickListener = this.renderer.listen("document",
            "mousedown",
            (event: any) => {
                if (this.ckeditor && !this.ckeditor.nativeElement.contains(event.target) && !event.target.id.startsWith("cke")) {
                    // Check for CKEditor 5 dialogs instead of CKEditor 4
                    const ckDialogs = document.querySelectorAll('.ck-balloon-panel, .ck-dialog');
                    let clickedInsideDialog = false;
                    
                    ckDialogs.forEach(dialog => {
                        if (dialog.contains(event.target)) {
                            clickedInsideDialog = true;
                        }
                    });

                    // Don't close edit mode if clicking inside CKEditor dialog
                    if (!clickedInsideDialog) {
                        this.editMode = false;
                        this.cdRef.detectChanges();
                        this.documentClickListener(); // remove it once we are done
                    }
                }
            });
    }

    getTitleClass(): string {
        if (this.item.itemConfigType === this.itemConfigType.CategoryItemConfig) {
            return "";
        } else {
            return "text-info";
        }
    }

    differentFromLive(itemDetail: IItemDetail): boolean {
        // get the live versions of this data
        const liveItemDetail = this.GetLiveVersionOfData(itemDetail.itemConfigId);
        if (liveItemDetail) {
            return liveItemDetail.value !== itemDetail.value;
        } else if (itemDetail && itemDetail.value) {
            return true;
        }
        return false;
    }

    private resyncMultipleSelectionsWithItemDetail(): void {
        // Filter out any placeholder items (id -1) before syncing
        const validItems = this.multipleSelectedDropdownItems.filter(ddi => ddi.id !== -1);
        this.item.itemDetail.value = validItems.map(ddi => ddi.id.toString()).join(",");
        this.item.itemDetail.codes = validItems.map(ddi => ddi.code).join(",");
    }

    private refreshMultipleSelectionString(): void {
        // Filter out any placeholder items (id -1) when displaying
        const validItems = this.multipleSelectedDropdownItems.filter(ddi => ddi.id !== -1);
        this.multipleSelectionString = validItems.map(ddi => ddi && ddi.text.toString()).join(", ");
        
        // If no valid items, show empty string to allow placeholder
        if (validItems.length === 0) {
            this.multipleSelectionString = "";
        }
    }

    private getLiveItem(itemConfigId: number): IItemDetail | null {
        // get the live versions of this data
        const liveItemDetail = this.GetLiveVersionOfData(itemConfigId);
        if (liveItemDetail && liveItemDetail.value && liveItemDetail.value.length > 0) {
            return liveItemDetail;
        } else {
            return null;
        }
    }

    /**
     *    Get the live item detail from the list based on an itemconfig id
     */
    private GetLiveVersionOfData(itemConfigId: number): IItemDetail | null {
        const itemDetails: IItemDetail[] = this.liveItems.filter((i: IItemDetail) => { return i.itemConfigId === itemConfigId; });
        if (itemDetails && itemDetails.length > 0) {
            return itemDetails[0];
        } else {
            return null;
        }
    }

    itemValueChanged(value: any): void {
        if (this.item.itemDetail && this.item.itemDetail.value !== this.originalValue) {
            this.item.itemDetail.modified = true;
        }
    }

    showLiveAudit(itemDetail: IItemDetail): void {      
        this.selectedItemDetailLiveItem = this.getLiveItem(itemDetail.itemConfigId);
        this.selectedItemDetailCurrentItem = itemDetail;        
        if (this.htmlContentModal) {
            this.htmlContentModal.openModal();
        }
    }

    /**
     * Fix CKEditor link dialog z-index and positioning issues
     */
    private fixCKEditorLinkDialog(): void {
        // Wait for CKEditor to be fully initialized
        setTimeout(() => {
            this.waitForCKEditorAndSetup();
        }, 1000);
    }

    private waitForCKEditorAndSetup(): void {
        let attempts = 0;
        const maxAttempts = 20; // Wait up to 10 seconds
        
        const checkForCKEditor = () => {
            attempts++;
            const ckElements = document.querySelectorAll('.ck-editor');
            const linkButton = document.querySelector('.ck-toolbar .ck-button[title*="Link"]');
            
            if (ckElements.length > 0 || attempts >= maxAttempts) {
                this.fixCKEditorZIndex();
                this.debugCKEditor();
                this.addCustomLinkHandler();
            } else {
                // Try again in 500ms
                setTimeout(checkForCKEditor, 500);
            }
        };
        
        checkForCKEditor();
    }

    private fixCKEditorZIndex(): void {
        // Ensure CKEditor elements appear above other elements
        const ckElements = [
            '.ck.ck-balloon-panel',
            '.ck-dialog-overlay',
            '.ck.ck-dialog',
            '.ck.ck-dropdown__panel',
            '.ck.ck-tooltip',
            '.ck.ck-link-form',
            '.ck.ck-link-actions'
        ];

        ckElements.forEach(selector => {
            const elements = document.querySelectorAll(selector);
            elements.forEach((element) => {
                const htmlElement = element as HTMLElement;
                if (htmlElement.style) {
                    htmlElement.style.zIndex = '9999';
                }
            });
        });

        // Also add event listener to fix z-index when CKEditor creates new elements
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as HTMLElement;
                            if (element.classList && element.classList.contains('ck')) {
                                element.style.zIndex = '9999';
                            }
                        }
                    });
                }
            });
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true
        });

        // Clean up observer after 30 seconds
        setTimeout(() => {
            observer.disconnect();
        }, 30000);
    }

    private debugCKEditor(): void {
        // More comprehensive search for link button
        const linkButtons = [
            document.querySelector('.ck-toolbar .ck-button[title*="Link"]'),
            document.querySelector('.ck-toolbar .ck-button[title*="link"]'),
            document.querySelector('.ck-toolbar .ck-button[data-cke-tooltip-text*="Link"]'),
            document.querySelector('.ck-toolbar .ck-button[aria-label*="Link"]')
        ];
        
        const linkButton = linkButtons.find(btn => btn !== null);
        
        if (linkButton) {
            // Add click event listener for proper link dialog positioning
            linkButton.addEventListener('click', (e) => {
                // Single timeout to position the dialog properly
                setTimeout(() => {
                    this.forceShowLinkDialog();
                }, 100);
            });
        }
    }

    private forceShowLinkDialog(): void {
        // Find the main link form/balloon panel
        const linkForm = document.querySelector('.ck-link-form') as HTMLElement;
        const balloonPanel = document.querySelector('.ck-balloon-panel') as HTMLElement;
        
        // Hide any duplicate or extra balloons first
        const allBalloons = document.querySelectorAll('.ck-balloon-panel');
        allBalloons.forEach((balloon, index) => {
            const element = balloon as HTMLElement;
            if (index > 0) { // Hide all except the first one
                element.style.display = 'none';
            }
        });
        
        // Hide only the navigation buttons, keep the dialog functional
        const navButtons = document.querySelectorAll('.ck-balloon-rotator__navigation');
        navButtons.forEach(button => {
            (button as HTMLElement).style.display = 'none';
        });

        // Style only the primary dialog (prefer linkForm over balloonPanel)
        const primaryDialog = linkForm || balloonPanel;
        
        if (primaryDialog) {
            // Simple z-index fix to make dialog visible
            primaryDialog.style.cssText += `
                z-index: 999999 !important;
                position: absolute !important;
                display: block !important;
                visibility: visible !important;
                opacity: 1 !important;
            `;
        }
    }

    private addCustomLinkHandler(): void {
        // Find the link button and add custom handler if default doesn't work
        const linkButton = document.querySelector('.ck-toolbar .ck-button[title*="Link"]') as HTMLElement;
        
        if (linkButton) {
            // Remove existing listeners and add our custom one
            const newLinkButton = linkButton.cloneNode(true) as HTMLElement;
            linkButton.parentNode?.replaceChild(newLinkButton, linkButton);
            
            newLinkButton.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                this.showCustomLinkDialog();
            });
        }
    }

    private showCustomLinkDialog(): void {
        const url = prompt('Enter the URL:');
        if (url) {
            const linkText = prompt('Enter the link text (or leave empty to use URL):') || url;
            
            // Insert HTML into CKEditor
            const editorElement = document.querySelector('.ck-editor .ck-editor__editable') as HTMLElement;
            if (editorElement) {
                // Focus the editor first
                editorElement.focus();
                
                // Get current selection
                const selection = window.getSelection();
                if (selection && selection.rangeCount > 0) {
                    const range = selection.getRangeAt(0);
                    
                    // Create link element
                    const link = document.createElement('a');
                    link.href = url;
                    link.textContent = linkText;
                    link.target = '_blank';
                    link.rel = 'noopener noreferrer';
                    
                    // Replace selection with link
                    range.deleteContents();
                    range.insertNode(link);
                    
                    // Update the model by triggering change event
                    const event = new Event('input', { bubbles: true });
                    editorElement.dispatchEvent(event);
                    
                    // Manually trigger value change detection
                    this.itemValueChanged(null);
                }
            }
        }
    }
}


